import {useId} from 'react';
import type {PluralFormsModel} from '@/types/content/pluralForms';
import type {Badge} from '@/types/loyalty';
import {twx} from '@/utils/tailwindUtils';

import BadgeImage from './BadgeImage';
import BadgeProgressBar from './BadgeProgressBar';

type BadgeCardProps = {
    badge: Badge;
    showProgress?: boolean;
    actionsRemainingPluralForms?: PluralFormsModel;
    className?: string;
    onClick?: () => void;
};

const BadgeCard = ({badge, showProgress = false, actionsRemainingPluralForms, className, onClick}: BadgeCardProps) => {
    const {title, description, imageUrl, totalSteps, finishedSteps} = badge;
    const titleId = useId();
    const descId = useId();

    const labelledBy = title ? titleId : undefined;
    const describedBy = description ? descId : undefined;

    return (
        <button
            type='button'
            onClick={onClick}
            data-testid='badge-card'
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
            className={twx(
                'relative flex w-full flex-col items-center gap-4 rounded-2xl bg-secondary p-4 text-center text-romance',
                'border-0 outline-none focus-visible:ring-2 focus-visible:ring-white/40',
                className
            )}
        >
            <BadgeImage imageUrl={imageUrl} size={122} />

            <div className='flex flex-col gap-2'>
                {title && (
                    <span id={titleId} className='m-0 font-bold text-18 leading-13 tracking-02'>
                        {title}
                    </span>
                )}
                {showProgress && typeof totalSteps === 'number' && typeof finishedSteps === 'number' && (
                    <BadgeProgressBar
                        totalSteps={totalSteps}
                        finishedSteps={finishedSteps}
                        actionsRemainingPluralForms={actionsRemainingPluralForms}
                        className='mt-1 w-full'
                    />
                )}
                {description && (
                    <p id={descId} className='m-0 text-12 text-romance/80 leading-167 tracking-02'>
                        {description}
                    </p>
                )}
            </div>
        </button>
    );
};

export default BadgeCard;
